import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AdminShell } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Plus, X, Trash, Copy, ArrowClockwise, Wrench, Envelope, Phone, Link as LinkIcon, PencilSimple, Receipt, FilePdf, CheckCircle } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import ESignPanel from "@/components/ESignPanel";

export default function AdminSubContractors() {
  const [subs, setSubs] = useState([]);
  const [openForm, setOpenForm] = useState(null);
  const [tab, setTab] = useState("subs"); // subs | invoices
  const [invoices, setInvoices] = useState({ items: [], total_pending: 0 });
  const [invFilter, setInvFilter] = useState("pending");

  const [counts, setCounts] = useState({});
  const [bfPlan, setBfPlan] = useState(null);
  const load = () => {
    api.get("/sub-contractors").then(r => setSubs(r.data));
    api.get("/vendors/record-counts").then(r => setCounts(r.data.counts || {})).catch(() => {});
  };
  const previewBackfill = async () => {
    try {
      const r = await api.post("/vendors/backfill-records", { dry_run: true });
      setBfPlan(r.data);
      toast.success(`${r.data.stats.to_create} new vendors · ${r.data.stats.jobs_to_link} WOs to link`);
    } catch (e) { toast.error(formatApiErrorDetail(e) || "Preview failed"); }
  };
  const applyBackfill = async () => {
    if (!window.confirm(`Create ${bfPlan.stats.to_create} vendor records and link ${bfPlan.stats.jobs_to_link} work orders?\n\nNew vendors are created with ALL emails/digests OFF and no email address — nothing is sent.`)) return;
    try {
      const r = await api.post("/vendors/backfill-records", { dry_run: false });
      toast.success(`Created ${r.data.created} vendors · linked ${r.data.jobs_linked} WOs`);
      setBfPlan(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e) || "Backfill failed"); }
  };
  const loadInvoices = () => api.get(`/sub-invoices?status=${invFilter}`).then(r => setInvoices(r.data)).catch(()=>{});
  useEffect(() => { load(); }, []);
  useEffect(() => { if (tab === "invoices") loadInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, invFilter]);
  const markPaid = async (jobId) => {
    if (!window.confirm("Mark this invoice as PAID?")) return;
    try { await api.post(`/sub-invoices/${jobId}/mark-paid`); toast.success("Marked paid"); loadInvoices(); }
    catch (e) { toast.error("Could not mark paid"); }
  };

  const copyLink = (token) => {
    const url = `${window.location.origin}/sub/${token}`;
    navigator.clipboard.writeText(url).then(() => toast.success("Portal link copied"));
  };
  const rotate = async (id) => {
    if (!window.confirm("Rotate this sub's magic link? The old link will stop working immediately.")) return;
    try { await api.post(`/sub-contractors/${id}/rotate-token`); toast.success("New link generated"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e) || "Rotate failed"); }
  };
  const remove = async (id, name) => {
    if (!window.confirm(`Delete ${name}? Their existing jobs stay put, but they'll no longer be assignable.`)) return;
    try { await api.delete(`/sub-contractors/${id}`); toast.success(`${name} removed`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e) || "Delete failed"); }
  };

  return (
    <AdminShell>
      <div className="p-4 sm:p-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight">Sub-Contractors</h1>
            <p className="text-sm text-slate-600 font-medium mt-1">External trades you assign jobs to. They get an email + private link — no login needed.</p>
          </div>
          <div className="flex gap-2 flex-wrap">
          <button onClick={previewBackfill} data-testid="vendor-backfill-btn"
            className="tactile-btn bg-slate-800 text-white h-12 px-4 font-black uppercase tracking-wide inline-flex items-center justify-center gap-2 text-xs">
            Backfill from WO History
          </button>
          <button onClick={() => setOpenForm("new")} data-testid="new-sub-btn"
            className="tactile-btn bg-[#00C4CC] text-white h-12 px-5 font-black uppercase tracking-wide inline-flex items-center justify-center gap-2">
            <Plus weight="bold" size={18}/> Add Sub-Contractor
          </button>
          </div>
        </div>

        {bfPlan?.ok && (
          <div className="mt-4 rugged-card bg-white p-4" data-testid="vendor-backfill-preview">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="font-display font-black text-sm uppercase tracking-wider">
                Vendor backfill preview — {bfPlan.stats.vendors} vendors found on WOs · <span className="text-emerald-700" data-testid="bf-to-create">{bfPlan.stats.to_create} new</span> · {bfPlan.stats.to_link_existing} already exist · <span data-testid="bf-jobs-to-link">{bfPlan.stats.jobs_to_link}</span> WOs to link
              </div>
              <div className="flex gap-2">
                <button onClick={() => setBfPlan(null)} className="h-9 px-3 border-2 border-slate-300 text-xs font-black uppercase tracking-widest">Cancel</button>
                <button onClick={applyBackfill} disabled={bfPlan.stats.to_create === 0 && bfPlan.stats.jobs_to_link === 0} data-testid="vendor-backfill-apply"
                  className="h-9 px-3 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white text-xs font-black uppercase tracking-widest">
                  Create &amp; Link — no emails sent
                </button>
              </div>
            </div>
            <div className="mt-2 max-h-56 overflow-auto text-xs font-mono divide-y divide-slate-100">
              {bfPlan.plan.map(p => (
                <div key={p.key} className="py-1 flex gap-3 items-center" data-testid={`bf-row-${p.key.replace(/\s+/g, "-")}`}>
                  <span className={`w-14 shrink-0 font-black uppercase ${p.action === "create" ? "text-emerald-700" : "text-slate-500"}`}>{p.action}</span>
                  <span className="flex-1 truncate">{p.vendor_name}{p.sub_name && p.sub_name !== p.vendor_name ? ` → ${p.sub_name}` : ""}</span>
                  <span className="w-20 text-right">{p.jobs} WOs</span>
                  <span className="w-16 text-right text-amber-700">{p.open} open</span>
                  <span className="w-20 text-right text-slate-500">{(p.last_job_at || "").slice(0, 10)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-6 flex gap-2 border-b-2 border-slate-200">
          <button onClick={() => setTab("subs")} data-testid="tab-subs"
            className={`px-4 py-2 font-black uppercase text-xs tracking-widest border-b-4 ${tab === "subs" ? "border-[#00C4CC] text-[#0e7490]" : "border-transparent text-slate-400 hover:text-slate-700"}`}>
            Subs
          </button>
          <button onClick={() => setTab("invoices")} data-testid="tab-invoices"
            className={`px-4 py-2 font-black uppercase text-xs tracking-widest border-b-4 inline-flex items-center gap-1.5 ${tab === "invoices" ? "border-[#00C4CC] text-[#0e7490]" : "border-transparent text-slate-400 hover:text-slate-700"}`}>
            <Receipt weight="bold" size={14}/> Sub Invoices
          </button>
        </div>

        {tab === "invoices" ? (
          <div className="mt-4" data-testid="sub-invoices-panel">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex gap-2">
                {["pending","paid","all"].map(k => (
                  <button key={k} onClick={() => setInvFilter(k)} data-testid={`inv-filter-${k}`}
                    className={`h-9 px-3 border-2 font-black uppercase text-[10px] tracking-widest ${invFilter === k ? "bg-[#0f172a] text-white border-[#0f172a]" : "bg-white text-slate-700 border-slate-300 hover:border-[#0f172a]"}`}>
                    {k}
                  </button>
                ))}
              </div>
              {invFilter === "pending" && invoices.total_pending > 0 && (
                <div className="text-sm font-black text-red-700">
                  Pending payout: <span className="font-mono">${invoices.total_pending.toFixed(2)}</span>
                </div>
              )}
            </div>
            <div className="mt-3 space-y-2">
              {(invoices.items || []).map(inv => (
                <div key={inv.job_id} className="rugged-card bg-white p-4 flex items-center gap-3 flex-wrap" data-testid={`inv-row-${inv.job_id}`}>
                  <div className="min-w-0 flex-1">
                    <Link to={`/admin/jobs/${inv.job_id}`} className="font-black hover:underline">{inv.title}</Link>
                    <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
                      {inv.is_adhoc && (
                        <span className={`text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded ${inv.is_pass_through ? "bg-amber-600 text-white" : "bg-slate-700 text-white"}`}>
                          {inv.is_pass_through ? "Pass-through" : "Ad-hoc"}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 truncate">
                      {inv.sub_name} {inv.sub_company && `· ${inv.sub_company}`}{inv.property_address ? ` · ${inv.property_address}` : ""}
                    </div>
                    <div className="text-[10px] font-black uppercase tracking-widest text-slate-400 mt-0.5">
                      Submitted {inv.submitted_at ? new Date(inv.submitted_at).toLocaleString() : "—"}
                      {inv.invoice_number && ` · Inv# ${inv.invoice_number}`}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-black font-mono text-lg">${(inv.amount || 0).toFixed(2)}</div>
                    {inv.quoted_price != null && inv.quoted_price !== inv.amount && (
                      <div className="text-[10px] font-black uppercase tracking-widest text-amber-600">
                        Quoted ${(inv.quoted_price || 0).toFixed(2)}
                      </div>
                    )}
                  </div>
                  {inv.pdf_path && (
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${encodeURIComponent(inv.pdf_path)}`} target="_blank" rel="noopener noreferrer"
                      data-testid={`inv-pdf-${inv.job_id}`}
                      className="h-9 px-3 border-2 border-slate-300 hover:border-[#0f172a] inline-flex items-center gap-1 font-black uppercase text-[10px] tracking-widest">
                      <FilePdf weight="bold" size={14}/> PDF
                    </a>
                  )}
                  {inv.paid_at ? (
                    <span className="h-9 px-3 bg-emerald-100 text-emerald-800 inline-flex items-center gap-1 font-black uppercase text-[10px] tracking-widest rounded">
                      <CheckCircle weight="fill" size={14}/> Paid {new Date(inv.paid_at).toLocaleDateString()}
                    </span>
                  ) : (
                    <button onClick={() => markPaid(inv.job_id)} data-testid={`mark-paid-${inv.job_id}`}
                      className="tactile-btn h-9 px-3 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase text-[10px] tracking-widest">
                      Mark Paid
                    </button>
                  )}
                </div>
              ))}
              {(invoices.items || []).length === 0 && (
                <div className="rugged-card p-10 text-center bg-white">
                  <Receipt weight="fill" size={40} className="mx-auto text-slate-300"/>
                  <p className="mt-3 font-bold text-slate-500">No {invFilter === "all" ? "" : invFilter} invoices.</p>
                </div>
              )}
            </div>
          </div>
        ) : (
        <div className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {subs.map(s => (
            <div key={s.id} data-testid={`sub-card-${s.id}`} className="rugged-card p-5 bg-white">
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <div className="font-display font-black text-xl truncate">{s.name}</div>
                  {s.company && <div className="text-xs font-black uppercase tracking-widest text-[#0f172a] mt-0.5 truncate">{s.company}</div>}
                  {s.trade && <div className="text-[10px] font-black uppercase tracking-widest text-[#0e7490] mt-0.5">{s.trade}</div>}
                </div>
                <div className="flex gap-1">
                  <button onClick={() => setOpenForm(s)} data-testid={`sub-edit-${s.id}`} className="p-1.5 text-slate-500 hover:text-[#0f172a]" title="Edit">
                    <PencilSimple weight="bold" size={16}/>
                  </button>
                  <button onClick={() => remove(s.id, s.name)} data-testid={`sub-delete-${s.id}`} className="p-1.5 text-slate-500 hover:text-red-500" title="Delete">
                    <Trash weight="bold" size={16}/>
                  </button>
                </div>
              </div>
              <div className="mt-3 space-y-1 text-sm">
                <div className="flex items-center gap-2 text-slate-700"><Envelope weight="bold" size={14} className="text-slate-400"/><span className="truncate">{s.email || <span className="text-slate-400 italic">no email yet</span>}</span></div>
                {s.phone && <div className="flex items-center gap-2 text-slate-700"><Phone weight="bold" size={14} className="text-slate-400"/><span>{s.phone}</span></div>}
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5 text-[10px] font-black uppercase tracking-widest">
                {counts[s.id] && (
                  <span className="px-1.5 py-0.5 bg-[#0f172a] text-white" data-testid={`sub-jobcount-${s.id}`} title={`Last WO ${(counts[s.id].last_job_at || "").slice(0, 10)}`}>
                    {counts[s.id].jobs} WOs{counts[s.id].open ? ` · ${counts[s.id].open} open` : ""}
                  </span>
                )}
                {!s.auto_email_on_assign && !s.auto_email_on_complete && !s.include_in_digest && <span className="px-1.5 py-0.5 bg-slate-200 text-slate-600">No emails</span>}
                {s.auto_email_on_assign && <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-800">Email on assign</span>}
                {s.auto_email_on_complete && <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-800">Email on complete</span>}
                {s.include_in_digest && <span className="px-1.5 py-0.5 bg-blue-100 text-blue-800">Wed digest</span>}
                {(s.default_flat_fee > 0) && <span className="px-1.5 py-0.5 bg-slate-200 text-slate-700">Flat ${s.default_flat_fee}</span>}
                {(s.hourly_rate > 0) && <span className="px-1.5 py-0.5 bg-slate-200 text-slate-700">${s.hourly_rate}/hr</span>}
              </div>
              <div className="mt-3 pt-3 border-t border-slate-200">
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Portal Link</div>
                <div className="flex gap-1">
                  <button onClick={() => copyLink(s.magic_token)} data-testid={`sub-copy-${s.id}`}
                    className="flex-1 h-9 px-2 border-2 border-[#0f172a] text-xs font-black uppercase tracking-wider hover:bg-[#0f172a] hover:text-white inline-flex items-center justify-center gap-1.5">
                    <Copy weight="bold" size={12}/> Copy Link
                  </button>
                  <button onClick={() => rotate(s.id)} data-testid={`sub-rotate-${s.id}`} title="Rotate link"
                    className="h-9 w-9 border-2 border-slate-300 text-slate-500 hover:text-red-500 flex items-center justify-center">
                    <ArrowClockwise weight="bold" size={14}/>
                  </button>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-slate-200">
                <ESignPanel
                  parentType="sub_contractor"
                  parentId={s.id}
                  defaultSubject={`Onboarding — ${s.name}${s.company ? ` (${s.company})` : ""}`}
                  defaultSigners={s.email ? [{ role: "Contractor", name: s.name || "", email_address: s.email }] : []}
                  compact={true}
                />
              </div>
            </div>
          ))}
          {subs.length === 0 && (
            <div className="col-span-full rugged-card p-10 text-center bg-white">
              <Wrench weight="fill" size={40} className="mx-auto text-slate-300"/>
              <p className="mt-3 font-bold text-slate-500">No sub-contractors yet.</p>
              <p className="text-sm text-slate-400">Add Leo (LCV Home Repairs), an electrician, painter — anyone you hand jobs to.</p>
            </div>
          )}
        </div>
        )}
      </div>
      {openForm && <SubForm sub={openForm === "new" ? null : openForm} onClose={() => setOpenForm(null)} onSaved={() => { setOpenForm(null); load(); }}/>}
    </AdminShell>
  );
}

function SubForm({ sub, onClose, onSaved }) {
  const [f, setF] = useState({
    name: sub?.name || "",
    email: sub?.email || "",
    phone: sub?.phone || "",
    company: sub?.company || "",
    trade: sub?.trade || "",
    notes: sub?.notes || "",
    default_flat_fee: sub?.default_flat_fee || 0,
    hourly_rate: sub?.hourly_rate || 0,
    auto_email_on_assign: sub?.auto_email_on_assign ?? true,
    auto_email_on_complete: sub?.auto_email_on_complete ?? true,
    include_in_digest: sub?.include_in_digest ?? true,
  });
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => setF(v => ({ ...v, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...f,
        default_flat_fee: Number(f.default_flat_fee) || 0,
        hourly_rate: Number(f.hourly_rate) || 0,
      };
      if (sub) await api.patch(`/sub-contractors/${sub.id}`, payload);
      else await api.post("/sub-contractors", payload);
      toast.success(sub ? "Sub updated" : "Sub added");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <form onSubmit={submit} className="w-full max-w-lg max-h-[92vh] overflow-y-auto bg-white border-2 border-[#0f172a] shadow-[8px_8px_0px_#0f172a] p-6" onClick={e => e.stopPropagation()} data-testid="sub-form">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-black text-2xl">{sub ? "Edit Sub-Contractor" : "New Sub-Contractor"}</h3>
          <button type="button" onClick={onClose}><X weight="bold" size={22}/></button>
        </div>
        <div className="mt-5 space-y-3">
          <Field label="Name *"><input required value={f.name} onChange={upd("name")} data-testid="sub-name" className="w-full h-11 px-2 border-2 border-[#0f172a]"/></Field>
          <Field label="Email *"><input required type="email" value={f.email} onChange={upd("email")} data-testid="sub-email" className="w-full h-11 px-2 border-2 border-[#0f172a]"/></Field>
          <Field label="Trade"><input value={f.trade} onChange={upd("trade")} placeholder="Electrician, Plumber, Painter…" data-testid="sub-trade" className="w-full h-11 px-2 border-2 border-[#0f172a]"/></Field>
          <Field label="Company"><input value={f.company} onChange={upd("company")} placeholder="LCV Home Repairs, ABC Electric…" data-testid="sub-company" className="w-full h-11 px-2 border-2 border-[#0f172a]"/></Field>
          <Field label="Phone"><input value={f.phone} onChange={upd("phone")} className="w-full h-11 px-2 border-2 border-[#0f172a]"/></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Default Flat Fee $"><input type="number" step="0.01" value={f.default_flat_fee} onChange={upd("default_flat_fee")} className="w-full h-11 px-2 border-2 border-[#0f172a]"/></Field>
            <Field label="Hourly Rate $"><input type="number" step="0.01" value={f.hourly_rate} onChange={upd("hourly_rate")} className="w-full h-11 px-2 border-2 border-[#0f172a]"/></Field>
          </div>
          <Field label="Notes"><textarea rows={3} value={f.notes} onChange={upd("notes")} className="w-full px-2 py-1.5 border-2 border-[#0f172a] resize-none"/></Field>
          <div className="pt-2 space-y-2">
            <Toggle checked={f.auto_email_on_assign} onChange={upd("auto_email_on_assign")} label="Email them when a job is assigned"/>
            <Toggle checked={f.auto_email_on_complete} onChange={upd("auto_email_on_complete")} label="Email them the PDF when the job is complete"/>
            <Toggle checked={f.include_in_digest} onChange={upd("include_in_digest")} label="Include in the Wednesday 3pm weekly digest"/>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="h-11 px-4 border-2 border-[#0f172a] font-black uppercase text-xs tracking-wider">Cancel</button>
          <button type="submit" disabled={busy} data-testid="sub-save-btn" className="tactile-btn h-11 px-5 bg-[#00C4CC] text-white font-black uppercase text-sm tracking-wider disabled:opacity-60">
            {busy ? "Saving…" : (sub ? "Save Changes" : "Add Sub-Contractor")}
          </button>
        </div>
      </form>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">{label}</span>
    {children}
  </label>
);
const Toggle = ({ checked, onChange, label }) => (
  <label className="flex items-center gap-2 cursor-pointer">
    <input type="checkbox" checked={checked} onChange={onChange} className="w-4 h-4 accent-[#00C4CC]"/>
    <span className="text-sm font-medium text-[#0f172a]">{label}</span>
  </label>
);
